import { useEffect, useMemo, useState } from 'react';
import { X, ArrowLeft, ChevronLeft, ChevronRight, Check, Calendar as CalendarIcon, Video, Clock } from 'lucide-react';
import { trackBookingConfirmed } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WEEKDAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const detectTimezone = () => {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected || detected === 'UTC') return 'Europe/Paris';
    return detected;
  } catch {
    return 'Europe/Paris';
  }
};

const formatDateLong = (dateStr) => {
  // dateStr = YYYY-MM-DD
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const wd = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'][date.getDay()];
  return `${wd} ${d} ${MONTHS_FR[m - 1].toLowerCase()} ${y}`;
};

const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const COMMON_TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Madrid',
  'America/New_York',
  'America/Los_Angeles',
  'America/Toronto',
  'Africa/Casablanca',
  'Africa/Abidjan',
  'Asia/Dubai',
  'UTC',
];

// ==================================
// Month view
// ==================================
const MonthView = ({ currentMonth, setCurrentMonth, availability, onSelect, selectedDate }) => {
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday = 0

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prev = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const next = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  return (
    <div className="flex flex-col gap-4" data-testid="month-view">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={prev}
          aria-label="Mois précédent"
          data-testid="prev-month-btn"
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(245, 239, 227, 0.06)',
            border: '1px solid rgba(245, 239, 227, 0.12)',
            color: '#F5EFE3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={18} />
        </button>
        <p style={{ color: '#F5EFE3', fontWeight: 600, fontSize: 16 }}>
          {MONTHS_FR[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </p>
        <button
          type="button"
          onClick={next}
          aria-label="Mois suivant"
          data-testid="next-month-btn"
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(245, 239, 227, 0.06)',
            border: '1px solid rgba(245, 239, 227, 0.12)',
            color: '#F5EFE3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center" style={{ color: 'rgba(245, 239, 227, 0.45)', fontSize: 12, fontWeight: 600 }}>
        {WEEKDAYS_FR.map((d, i) => <div key={i}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} />;
          const key = toDateKey(cell);
          const isPast = cell < today;
          const slots = availability[key] || [];
          const isAvailable = !isPast && slots.length > 0;
          const isSelected = selectedDate === key;
          return (
            <button
              key={i}
              type="button"
              disabled={!isAvailable}
              onClick={() => isAvailable && onSelect(key)}
              data-testid={`day-${key}`}
              style={{
                aspectRatio: '1 / 1',
                minHeight: 44,
                borderRadius: 10,
                border: isSelected
                  ? '2px solid #F5EFE3'
                  : isAvailable
                  ? '1px solid rgba(245, 239, 227, 0.15)'
                  : '1px solid transparent',
                background: isSelected
                  ? 'rgba(245, 239, 227, 0.12)'
                  : isAvailable
                  ? 'rgba(245, 239, 227, 0.04)'
                  : 'transparent',
                color: isAvailable ? '#F5EFE3' : 'rgba(245, 239, 227, 0.25)',
                fontSize: 15,
                fontWeight: isSelected ? 700 : 500,
                cursor: isAvailable ? 'pointer' : 'not-allowed',
              }}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ==================================
// Calendar + Slots combined view (Calendly-style)
// ==================================
const CalendarView = ({
  currentMonth, setCurrentMonth, availability,
  onSelectDate, selectedDate, slots, onSelectSlot,
}) => (
  <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-testid="calendar-view">
    {/* Calendar (left on desktop) */}
    <div className="flex-1 min-w-0">
      <MonthView
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        availability={availability}
        onSelect={onSelectDate}
        selectedDate={selectedDate}
      />
    </div>

    {/* Slots (right on desktop, below on mobile) */}
    <div
      className="md:w-56 md:shrink-0 md:pl-8 md:border-l md:border-[rgba(245,239,227,0.1)]"
      data-testid="slots-panel"
    >
      {selectedDate ? (
        <div className="flex flex-col gap-3">
          <p style={{ color: '#F5EFE3', fontWeight: 600, fontSize: 15 }}>
            {formatDateLong(selectedDate)}
          </p>
          {slots.length > 0 ? (
            <div className="flex flex-col gap-2">
              {slots.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => onSelectSlot(slot)}
                  data-testid={`slot-${slot}`}
                  className="w-full"
                  style={{
                    minHeight: 48,
                    borderRadius: 10,
                    border: '1px solid rgba(245, 239, 227, 0.18)',
                    background: 'rgba(245, 239, 227, 0.04)',
                    color: '#F5EFE3',
                    fontSize: 15,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  {slot}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgba(245, 239, 227, 0.55)', fontSize: 14 }}>
              Aucun créneau disponible.
            </p>
          )}
        </div>
      ) : (
        <p style={{ color: 'rgba(245, 239, 227, 0.45)', fontSize: 14, marginTop: 8 }}>
          Sélectionne un jour à gauche pour voir les créneaux.
        </p>
      )}
    </div>
  </div>
);

// ==================================
// Confirm view
// ==================================
const ConfirmView = ({ selectedDate, selectedSlot, timezone, onChangeSlot, onConfirm, loading, error }) => (
  <div className="flex flex-col gap-5" data-testid="confirm-view">
    <button
      type="button"
      onClick={onChangeSlot}
      data-testid="change-slot-btn"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        color: '#F5EFE3', fontSize: 15, fontWeight: 500,
        background: 'transparent', border: 'none', cursor: 'pointer',
        alignSelf: 'flex-start', padding: 0,
      }}
    >
      <ArrowLeft size={16} /> Changer de créneau
    </button>

    <div
      className="card-forest flex flex-col gap-4"
      style={{ padding: 20 }}
    >
      <div className="flex items-start gap-3">
        <CalendarIcon size={18} style={{ marginTop: 3, color: '#F5EFE3' }} strokeWidth={2} />
        <div>
          <p style={{ color: 'rgba(245, 239, 227, 0.55)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600 }}>Créneau</p>
          <p style={{ color: '#F5EFE3', fontSize: 16, fontWeight: 600, marginTop: 4 }}>
            {formatDateLong(selectedDate)} · {selectedSlot}
          </p>
          <p style={{ color: 'rgba(245, 239, 227, 0.55)', fontSize: 14, marginTop: 2 }}>
            Fuseau : {timezone}
          </p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Video size={18} style={{ marginTop: 3, color: '#F5EFE3' }} strokeWidth={2} />
        <p style={{ color: 'rgba(245, 239, 227, 0.85)', fontSize: 15, lineHeight: 1.5 }}>
          L&rsquo;appel se fera sur Google Meet. Tu recevras le lien par email.
        </p>
      </div>
    </div>

    <p style={{ color: 'rgba(245, 239, 227, 0.55)', fontSize: 14, lineHeight: 1.55 }}>
      Si tu ne peux pas être là, préviens-moi à l&rsquo;avance.
    </p>

    {error && (
      <p style={{ color: '#F5EFE3', fontSize: 14 }} data-testid="booking-error">
        {error}
      </p>
    )}

    <button
      type="button"
      onClick={onConfirm}
      disabled={loading}
      className="btn-primary w-full"
      data-testid="confirm-booking-btn"
    >
      {loading ? 'Confirmation…' : 'Confirmer le rendez-vous'}
    </button>
  </div>
);

// ==================================
// Optional message view (after confirmation)
// ==================================
const AfterMessageView = ({ onSubmit, onSkip, loading }) => {
  const [msg, setMsg] = useState('');
  return (
    <div className="flex flex-col gap-4" data-testid="after-message-view">
      <div className="flex flex-col gap-2 items-center text-center">
        <p className="microlabel">C&rsquo;est confirmé</p>
        <h2 style={{ color: '#F5EFE3', fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 22, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
          Une dernière chose, si tu veux
        </h2>
        <p style={{ color: 'rgba(245, 239, 227, 0.72)', fontSize: 15, lineHeight: 1.55, maxWidth: 340 }}>
          Dis-moi en deux lignes où tu en es. Ça me permet de préparer notre appel.
        </p>
      </div>
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        placeholder="Ta situation actuelle, tes objectifs, tes blocages…"
        rows={4}
        data-testid="message-input"
        style={{
          width: '100%',
          padding: '14px 16px',
          background: 'rgba(245, 239, 227, 0.06)',
          border: '1px solid rgba(245, 239, 227, 0.15)',
          borderRadius: 12,
          color: '#F5EFE3',
          fontSize: 16,
          outline: 'none',
          fontFamily: 'Inter, sans-serif',
          resize: 'vertical',
          minHeight: 100,
        }}
      />
      <button
        type="button"
        onClick={() => onSubmit(msg)}
        disabled={loading || !msg.trim()}
        className="btn-primary w-full"
        data-testid="submit-message-btn"
      >
        {loading ? 'Envoi…' : 'Envoyer'}
      </button>
      <button
        type="button"
        onClick={onSkip}
        data-testid="skip-message-btn"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(245, 239, 227, 0.55)',
          fontSize: 15,
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: 8,
        }}
      >
        Passer
      </button>
    </div>
  );
};

// ==================================
// Main
// ==================================
export const BookingCalendar = ({ qualificationData, onClose, onBooked }) => {
  const [view, setView] = useState('calendar'); // calendar | slots | confirm | message | done
  const [availability, setAvailability] = useState({});
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [timezone, setTimezone] = useState(() => detectTimezone());
  const [tzEditing, setTzEditing] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/availability`);
        const data = await res.json();
        // Normalize into { 'YYYY-MM-DD': ['HH:mm', ...] }
        const map = {};
        const src = data.availability || {};
        if (Array.isArray(src)) {
          src.forEach((entry) => {
            if (entry && entry.date && Array.isArray(entry.slots) && entry.slots.length > 0) {
              map[entry.date] = entry.slots;
            }
          });
        } else if (typeof src === 'object') {
          Object.entries(src).forEach(([date, slots]) => {
            if (Array.isArray(slots) && slots.length > 0) {
              map[date] = slots;
            }
          });
        }
        setAvailability(map);
      } catch (e) {
        setError('Impossible de charger les disponibilités.');
      }
    };
    load();
  }, []);

  const slotsForSelected = useMemo(
    () => (selectedDate ? availability[selectedDate] || [] : []),
    [selectedDate, availability]
  );

  const handleSelectDate = (dateKey) => {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
  };
  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    setView('confirm');
  };
  const handleChangeSlot = () => setView('calendar');
  const handleChangeDay = () => setView('calendar');

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qualification: qualificationData,
          booking_date: selectedDate,
          booking_time: selectedSlot,
          phone: qualificationData.phone || '',
          timezone,
          utm_source: qualificationData.utm_source || '',
          utm_medium: qualificationData.utm_medium || '',
          utm_campaign: qualificationData.utm_campaign || '',
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        setError(result.detail || 'Erreur lors de la confirmation.');
        return;
      }
      setBookingId(result.booking_id);
      trackBookingConfirmed();
      if (onBooked) onBooked(result);
      setView('message');
    } catch (e) {
      setError('Erreur réseau. Réessaie.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMessage = async (msg) => {
    if (!bookingId) {
      setView('done');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API}/api/bookings/${bookingId}/message`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
    } catch (e) {
      // silent — this step is optional
    } finally {
      setLoading(false);
      setView('done');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.75)' }}
      onClick={onClose}
      data-testid="booking-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md md:max-w-2xl overflow-hidden"
        style={{
          background: '#0D1F17',
          border: '1px solid rgba(245, 239, 227, 0.12)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between" style={{ padding: '20px 20px 8px 20px' }}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="booking-close-btn"
            style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(245, 239, 227, 0.06)',
              border: '1px solid rgba(245, 239, 227, 0.12)',
              color: '#F5EFE3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
          <p style={{ color: '#F5EFE3', fontSize: 15, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Étape 2 / 2
          </p>
          <div style={{ width: 40 }} />
        </div>

        <div style={{ padding: '12px 20px 24px 20px' }}>
          {/* Timezone bar */}
          {view !== 'message' && view !== 'done' && (
            <div className="flex items-center justify-between mb-4" style={{
              background: 'rgba(245, 239, 227, 0.04)',
              border: '1px solid rgba(245, 239, 227, 0.12)',
              borderRadius: 10,
              padding: '10px 12px',
            }}>
              <div className="flex items-center gap-2" style={{ color: 'rgba(245, 239, 227, 0.72)', fontSize: 13 }}>
                <Clock size={14} strokeWidth={2} />
                <span>Fuseau :</span>
              </div>
              {tzEditing ? (
                <select
                  value={timezone}
                  onChange={(e) => { setTimezone(e.target.value); setTzEditing(false); }}
                  onBlur={() => setTzEditing(false)}
                  data-testid="timezone-select"
                  autoFocus
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    color: '#F5EFE3',
                    fontSize: 13,
                    border: '1px solid rgba(245, 239, 227, 0.2)',
                    borderRadius: 6,
                    padding: '4px 8px',
                  }}
                >
                  {[timezone, ...COMMON_TIMEZONES.filter(t => t !== timezone)].map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => setTzEditing(true)}
                  data-testid="timezone-edit-btn"
                  style={{
                    color: '#F5EFE3',
                    fontSize: 13,
                    background: 'transparent',
                    border: 'none',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {timezone}
                </button>
              )}
            </div>
          )}

          {view === 'calendar' && (
            <CalendarView
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              availability={availability}
              onSelectDate={handleSelectDate}
              selectedDate={selectedDate}
              slots={slotsForSelected}
              onSelectSlot={handleSelectSlot}
            />
          )}
          {view === 'confirm' && (
            <ConfirmView
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              timezone={timezone}
              onChangeSlot={handleChangeSlot}
              onConfirm={handleConfirm}
              loading={loading}
              error={error}
            />
          )}
          {view === 'message' && (
            <AfterMessageView
              onSubmit={handleSubmitMessage}
              onSkip={() => setView('done')}
              loading={loading}
            />
          )}
          {view === 'done' && (
            <div className="flex flex-col items-center text-center gap-5" data-testid="booking-done-view" style={{ padding: '12px 0' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'rgba(245, 239, 227, 0.08)',
                border: '1px solid rgba(245, 239, 227, 0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#F5EFE3',
              }}>
                <Check size={28} strokeWidth={2.5} />
              </div>
              <h2 style={{ color: '#F5EFE3', fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.02em' }}>
                Rendez-vous confirmé
              </h2>
              <p style={{ color: 'rgba(245, 239, 227, 0.72)', fontSize: 15, lineHeight: 1.55, maxWidth: 340 }}>
                On se voit le <strong style={{ color: '#F5EFE3' }}>{formatDateLong(selectedDate)}</strong> à <strong style={{ color: '#F5EFE3' }}>{selectedSlot}</strong>. Tu recevras le lien Google Meet par email.
              </p>
              <button onClick={onClose} className="btn-primary w-full" data-testid="close-done-btn">
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
